/**
 * rbac.ts — Client-side RBAC helpers.
 *
 * W36-B fix: Bryan K. Short has role='manager' with isDualRole=true, which
 * grants executive-level access. Predicates that only check role==='executive'
 * were incorrectly blocking him from executive views.
 *
 * The canonical predicate for "user has executive access" is:
 *   hasExecutiveAccess(user) — true for role==='executive' OR manager+isDualRole
 */
import type { SessionUser } from "./session";

/**
 * Returns true when the user has executive-level access.
 * Covers:
 *   - role === 'executive'
 *   - role === 'manager' && isDualRole === true  (e.g. Bryan K. Short)
 *   - future: Array.isArray(user.roles) && user.roles.includes('executive')
 */
export function hasExecutiveAccess(user: SessionUser | null | undefined): boolean {
  if (!user) return false;
  if (user.role === "executive") return true;
  if (user.role === "manager" && user.isDualRole) return true;
  return false;
}

/**
 * Returns true when the user has manager-or-executive access.
 * Covers role === 'manager', role === 'executive', and dual-role managers.
 */
export function hasManagerAccess(user: SessionUser | null | undefined): boolean {
  if (!user) return false;
  if (user.role === "manager" || user.role === "executive") return true;
  return false;
}
